// Dixon & Coles (1997): model the goals, not the result.
//
// Elo rates a team by whether it won. In a low-scoring sport that throws most
// of the information away — a 1-0 and a 4-0 are the same row — and it can
// never price a draw, which is a quarter of every Premier League season. So
// soccer gets its own model: each side has an attack and a defence in log
// goals, and the two scorelines are Poisson.
//
//   lambda = exp(attack_home - defence_away + homeAdv)   goals for the home side
//   mu     = exp(attack_away - defence_home)             goals for the away side
//
// Independent Poissons are very nearly right, and wrong in exactly one place:
// 0-0, 1-0, 0-1 and 1-1 happen more often than independence says, because a
// tight game is tight for both teams. Dixon and Coles patch those four cells
// with a single parameter:
//
//   tau(0,0) = 1 - lambda*mu*rho   tau(0,1) = 1 + lambda*rho
//   tau(1,0) = 1 + mu*rho          tau(1,1) = 1 - rho        tau = 1 elsewhere
//
// and P(x, y) = tau(x, y) * Pois(x; lambda) * Pois(y; mu). A negative rho —
// which is what the data says — lifts 0-0 and 1-1 and shaves the 1-0s.
//
// Form is handled by weight rather than by a window: every historical match
// carries w = exp(-xi * daysBefore(asOf)), so last month counts nearly whole
// and three seasons ago counts for very little. xi is the one parameter worth
// tuning, and `tune` tunes it walk-forward.
//
// Fitting maximises the weighted log-likelihood. The gradients of the Poisson
// part are closed form —
//
//   d/d attack_home  = w * (x - lambda)     d/d defence_away = w * (lambda - x)
//   d/d attack_away  = w * (y - mu)         d/d defence_home = w * (mu - y)
//   d/d homeAdv      = w * (x - lambda)
//
// — and each of those gradients has a closed-form root when the other blocks
// are held fixed, because the likelihood in one rating is G*a - S*exp(a): the
// weighted goals a team scored against the weighted exposure it scored them
// from. So instead of stepping along the gradient with a rate that has to be
// guessed, each block jumps straight to its own maximum (attacks, then
// defences, then the home advantage), which is block coordinate ascent: every
// sweep raises the likelihood, and it converges in a few dozen sweeps rather
// than a few thousand steps. `learningRate` survives as a relaxation factor on
// that jump — 1 takes the whole step.
//
// rho does not enter the Poisson part at all, so it is refined separately by a
// golden-section search on the tau term, alternating with the ratings.
//
// Identifiability: adding the same constant to every attack AND every defence
// leaves lambda and mu untouched (defence enters with a minus sign, so the
// free direction is both moving together, not in opposite directions). Each
// sweep therefore re-centres on mean(attack) = 0, which is the convention the
// published ratings are read in.
//
// There is no `update`. Elo can take one result and move two ratings; a
// maximum-likelihood fit cannot be nudged that way, and a refit is cheap
// (tens of milliseconds on five seasons), so a backtest should set
// `refitEvery` rather than look for an incremental step here.

import { closingView } from './closeLine.js';
import { assertBefore } from './base.js';

/** The soccer competitions in `SPORTS`. History and live prices agree on these ids. */
export const DC_SPORTS = ['epl', 'aleague'];

const DEFAULT_PARAMS = {
  xi: 0.0018,        // time decay per day; 0.0018 halves a match's weight in ~a year
  maxGoals: 10,      // score matrix is 0..maxGoals a side
  iterations: 200,   // cap on ratings sweeps per round; convergence usually ends it first
  learningRate: 1,   // relaxation on the exact block step; 1 takes all of it
  rho: null,         // fitted
  homeAdv: null,     // fitted
  target: 'result',  // or 'close' — see DC_TARGETS
};

/**
 * WHAT THE DECAY RATE IS CHOSEN TO PREDICT (F2).
 *
 * The likelihood itself cannot move: it is over SCORELINES, and a closing price
 * is not a scoreline — there is no 2-1 to read off a market. So unlike Elo,
 * `target: 'close'` changes nothing about what `fit` maximises, and pretending
 * otherwise would be a number nobody measured.
 *
 * What it does change is `tune`. Under `result` a decay rate is chosen by the
 * multiclass log loss on which of home, draw or away actually happened; under
 * `close` it is chosen by the cross-entropy against the market's own devigged
 * CLOSING distribution over the same three. That is a far less noisy target —
 * one match is a single draw from the distribution, the close is an estimate of
 * it — and it is the honest half of the loss-function fix for a goals model.
 *
 * The DEFAULT STAYS `result`.
 */
export const DC_TARGETS = ['result', 'close'];

const DEFAULT_GRID = { xi: [0.001, 0.0018, 0.003] };

const DAY_MS = 86_400_000;
const RHO_BOUND = 0.2;        // the search interval Dixon-Coles rho is looked for in
const TAU_FLOOR = 0.01;       // no tau cell may fall below this, so no P(x, y) goes negative
const RATING_CLAMP = 3;       // log goals; e^3 is 20x, far outside anything real
const HOME_ADV_CLAMP = 1;
const INIT_HOME_ADV = 0.25;
const INIT_RHO = -0.05;
const CONVERGED = 1e-9;
const TINY = 1e-9;

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
const byKickoff = (a, b) => String(a.kickoff).localeCompare(String(b.kickoff));
const seasonOf = (match) => String(match?.season ?? new Date(match?.kickoff).getUTCFullYear());

/** The low-score correction. 1 everywhere but the four cells Dixon and Coles found. */
export function tau(x, y, lambda, mu, rho) {
  if (x === 0 && y === 0) return 1 - lambda * mu * rho;
  if (x === 0 && y === 1) return 1 + lambda * rho;
  if (x === 1 && y === 0) return 1 + mu * rho;
  if (x === 1 && y === 1) return 1 - rho;
  return 1;
}

/**
 * The rho interval that keeps every tau cell at or above TAU_FLOOR for one
 * pair of rates, intersected with the interval rho is searched in.
 */
export function rhoBounds(lambda, mu) {
  const slack = 1 - TAU_FLOOR;
  const lo = Math.max(-slack / lambda, -slack / mu, -RHO_BOUND);
  const hi = Math.min(slack / (lambda * mu), slack, RHO_BOUND);
  return lo > hi ? [lo, lo] : [lo, hi];
}

/** rho, held inside the range that keeps this match's tau cells positive. */
export function clampRho(rho, lambda, mu) {
  const [lo, hi] = rhoBounds(lambda, mu);
  return clamp(Number.isFinite(rho) ? rho : 0, lo, hi);
}

/** Pois(0..maxGoals; lambda), built up from P(0) so there are no factorials. */
function poissonVector(lambda, maxGoals) {
  const out = new Array(maxGoals + 1);
  let p = Math.exp(-lambda);
  out[0] = p;
  for (let k = 1; k <= maxGoals; k++) {
    p = (p * lambda) / k;
    out[k] = p;
  }
  return out;
}

/** A finished match's goals, or null if it has none to learn from. */
function goalsOf(match) {
  const result = match?.result;
  if (!result || result.status === 'scheduled') return null;
  const x = result.homeScore;
  const y = result.awayScore;
  if (!Number.isFinite(x) || !Number.isFinite(y) || x < 0 || y < 0) return null;
  return [x, y];
}

/** Scoring rates for one fixture, or null if either side is not in the state. */
function ratesFor(state, match) {
  const attack = state?.attack ?? {};
  const defence = state?.defence ?? {};
  const home = match?.home?.id;
  const away = match?.away?.id;
  if (home == null || away == null) return null;
  if (!Object.hasOwn(attack, home) || !Object.hasOwn(attack, away)) return null;
  const homeAdv = Number.isFinite(state?.homeAdv) ? state.homeAdv : INIT_HOME_ADV;
  return {
    lambda: Math.exp((attack[home] ?? 0) - (defence[away] ?? 0) + homeAdv),
    mu: Math.exp((attack[away] ?? 0) - (defence[home] ?? 0)),
  };
}

/**
 * P(homeGoals = x, awayGoals = y) for every scoreline up to `maxGoals` a side,
 * tau applied and the whole thing renormalised so it sums to 1. `matrix[x][y]`.
 *
 * Null when either side is a team the state has never seen. Exported because
 * every soccer market a later slice wants — totals, both-teams-to-score,
 * correct score — is a different sum over this one object.
 */
export function scoreMatrix(state, match) {
  const rates = ratesFor(state, match);
  if (!rates) return null;
  const { lambda, mu } = rates;
  const maxGoals = Number.isInteger(state?.params?.maxGoals) && state.params.maxGoals > 0
    ? state.params.maxGoals
    : DEFAULT_PARAMS.maxGoals;
  const rho = clampRho(state?.rho ?? 0, lambda, mu);

  const px = poissonVector(lambda, maxGoals);
  const py = poissonVector(mu, maxGoals);
  const matrix = [];
  let total = 0;
  for (let x = 0; x <= maxGoals; x++) {
    const row = new Array(maxGoals + 1);
    for (let y = 0; y <= maxGoals; y++) {
      const cell = px[x] * py[y] * tau(x, y, lambda, mu, rho);
      row[y] = cell;
      total += cell;
    }
    matrix.push(row);
  }
  if (!(total > 0)) return null;
  for (const row of matrix) {
    for (let y = 0; y <= maxGoals; y++) row[y] /= total;
  }
  return matrix;
}

/**
 * The markets a score matrix implies: the three-way result, over/under 2.5
 * goals, and both teams to score. Home wins are below the diagonal, the draw
 * is the trace, away wins are above.
 */
export function marketProbs(matrix) {
  if (!matrix) return null;
  let home = 0;
  let draw = 0;
  let away = 0;
  let over25 = 0;
  let btts = 0;
  for (let x = 0; x < matrix.length; x++) {
    const row = matrix[x];
    for (let y = 0; y < row.length; y++) {
      const p = row[y];
      if (x > y) home += p;
      else if (x === y) draw += p;
      else away += p;
      if (x + y >= 3) over25 += p;
      if (x >= 1 && y >= 1) btts += p;
    }
  }
  return { home, draw, away, over25, under25: 1 - over25, btts };
}

// --- fitting -----------------------------------------------------------------

/**
 * One fixed-length, allocation-free view of the training set: team ids resolved
 * to indices, goals and decay weights in typed arrays, and the low-scoring
 * matches picked out because they are the only ones rho can be read from.
 */
function prepare(rows, asOfMs, xi) {
  const ids = [];
  const index = new Map();
  const homeIdx = [];
  const awayIdx = [];
  const goalsHome = [];
  const goalsAway = [];
  const weights = [];
  const competitions = new Set();

  const idOf = (id) => {
    let i = index.get(id);
    if (i === undefined) {
      i = ids.length;
      ids.push(id);
      index.set(id, i);
    }
    return i;
  };

  for (const match of rows.slice().sort(byKickoff)) {
    const goals = goalsOf(match);
    if (!goals) continue;
    const home = match.home?.id;
    const away = match.away?.id;
    if (home == null || away == null) continue;
    const kickoff = new Date(match.kickoff).getTime();
    if (!Number.isFinite(kickoff)) continue;
    homeIdx.push(idOf(home));
    awayIdx.push(idOf(away));
    goalsHome.push(goals[0]);
    goalsAway.push(goals[1]);
    weights.push(Math.exp(-xi * Math.max(0, (asOfMs - kickoff) / DAY_MS)));
    if (match.competition) competitions.add(match.competition);
  }

  const n = homeIdx.length;
  const lowIdx = [];
  for (let i = 0; i < n; i++) {
    if (goalsHome[i] <= 1 && goalsAway[i] <= 1) lowIdx.push(i);
  }

  return {
    ids,
    teams: ids.length,
    n,
    home: Int32Array.from(homeIdx),
    away: Int32Array.from(awayIdx),
    x: Float64Array.from(goalsHome),
    y: Float64Array.from(goalsAway),
    w: Float64Array.from(weights),
    low: Int32Array.from(lowIdx),
    competition: competitions.size === 1 ? [...competitions][0] : null,
  };
}

/**
 * Maximise `f` on `[lo, hi]` by golden section. No derivative, no step size,
 * and the bracket shrinks by a constant factor every call — 80 of them takes
 * a 0.4-wide interval well below any precision rho is read at.
 */
function goldenSection(f, lo, hi, iterations = 80) {
  const invPhi = (Math.sqrt(5) - 1) / 2;
  let a = lo;
  let b = hi;
  if (!(b > a)) return a;
  let c = b - invPhi * (b - a);
  let d = a + invPhi * (b - a);
  let fc = f(c);
  let fd = f(d);
  for (let i = 0; i < iterations; i++) {
    if (fc > fd) {
      b = d;
      d = c;
      fd = fc;
      c = b - invPhi * (b - a);
      fc = f(c);
    } else {
      a = c;
      c = d;
      fc = fd;
      d = a + invPhi * (b - a);
      fd = f(d);
    }
    if (b - a < 1e-7) break;
  }
  return (a + b) / 2;
}

/**
 * The fit itself, on an already-filtered history. Kept separate from `fit` so
 * `tune` can refit a hundred times without re-running the lookahead check on
 * the same rows.
 */
function fitCore(rows, asOf, p) {
  const asOfMs = new Date(asOf).getTime();
  const data = prepare(rows, asOfMs, p.xi);
  const { n, teams } = data;

  const attack = new Float64Array(teams);
  const defence = new Float64Array(teams);
  const expA = new Float64Array(teams).fill(1);
  const expD = new Float64Array(teams).fill(1);
  let homeAdv = INIT_HOME_ADV;
  let rho = INIT_RHO;

  if (n > 0 && teams > 0) {
    // Weighted goals scored and conceded never change: they are the numerators
    // of every ratings update, so they are accumulated once.
    const scored = new Float64Array(teams);
    const conceded = new Float64Array(teams);
    let homeGoals = 0;
    for (let i = 0; i < n; i++) {
      const h = data.home[i];
      const a = data.away[i];
      const w = data.w[i];
      scored[h] += w * data.x[i];
      scored[a] += w * data.y[i];
      conceded[a] += w * data.x[i];
      conceded[h] += w * data.y[i];
      homeGoals += w * data.x[i];
    }

    const mass = new Float64Array(teams);
    const lowLambda = new Float64Array(data.low.length);
    const lowMu = new Float64Array(data.low.length);

    const step = (current, target) => clamp(
      current + p.learningRate * (clamp(target, -RATING_CLAMP, RATING_CLAMP) - current),
      -RATING_CLAMP,
      RATING_CLAMP,
    );

    for (let round = 0; round < 3; round++) {
      // --- the Poisson part: attacks, then defences, then the home advantage.
      for (let iter = 0; iter < p.iterations; iter++) {
        let moved = 0;
        const eH = Math.exp(homeAdv);

        // attack_t: exposure is every opponent defence it scored against.
        mass.fill(0);
        for (let i = 0; i < n; i++) {
          const h = data.home[i];
          const a = data.away[i];
          const w = data.w[i];
          mass[h] += (w * eH) / expD[a];
          mass[a] += w / expD[h];
        }
        for (let t = 0; t < teams; t++) {
          const target = Math.log(Math.max(scored[t], TINY)) - Math.log(Math.max(mass[t], TINY));
          const next = step(attack[t], target);
          moved = Math.max(moved, Math.abs(next - attack[t]));
          attack[t] = next;
          expA[t] = Math.exp(next);
        }

        // defence_t: exposure is every opponent attack it conceded to.
        mass.fill(0);
        for (let i = 0; i < n; i++) {
          const h = data.home[i];
          const a = data.away[i];
          const w = data.w[i];
          mass[a] += w * expA[h] * eH;
          mass[h] += w * expA[a];
        }
        for (let t = 0; t < teams; t++) {
          const target = Math.log(Math.max(mass[t], TINY)) - Math.log(Math.max(conceded[t], TINY));
          const next = step(defence[t], target);
          moved = Math.max(moved, Math.abs(next - defence[t]));
          defence[t] = next;
          expD[t] = Math.exp(next);
        }

        let homeMass = 0;
        for (let i = 0; i < n; i++) homeMass += (data.w[i] * expA[data.home[i]]) / expD[data.away[i]];
        const targetH = Math.log(Math.max(homeGoals, TINY)) - Math.log(Math.max(homeMass, TINY));
        const nextH = clamp(
          homeAdv + p.learningRate * (clamp(targetH, -HOME_ADV_CLAMP, HOME_ADV_CLAMP) - homeAdv),
          -HOME_ADV_CLAMP,
          HOME_ADV_CLAMP,
        );
        moved = Math.max(moved, Math.abs(nextH - homeAdv));
        homeAdv = nextH;

        // Re-centre on mean(attack) = 0. Both ratings move the same way, which
        // is the direction lambda and mu cannot see.
        let sum = 0;
        for (let t = 0; t < teams; t++) sum += attack[t];
        const shift = -sum / teams;
        if (shift !== 0) {
          for (let t = 0; t < teams; t++) {
            attack[t] = clamp(attack[t] + shift, -RATING_CLAMP, RATING_CLAMP);
            defence[t] = clamp(defence[t] + shift, -RATING_CLAMP, RATING_CLAMP);
            expA[t] = Math.exp(attack[t]);
            expD[t] = Math.exp(defence[t]);
          }
        }

        if (moved < CONVERGED) break;
      }

      // --- rho, on the four cells it is the only thing that moves.
      const eH = Math.exp(homeAdv);
      let lo = -RHO_BOUND;
      let hi = RHO_BOUND;
      for (let i = 0; i < n; i++) {
        const lambda = (expA[data.home[i]] * eH) / expD[data.away[i]];
        const mu = expA[data.away[i]] / expD[data.home[i]];
        const [matchLo, matchHi] = rhoBounds(lambda, mu);
        if (matchLo > lo) lo = matchLo;
        if (matchHi < hi) hi = matchHi;
      }
      for (let k = 0; k < data.low.length; k++) {
        const i = data.low[k];
        lowLambda[k] = (expA[data.home[i]] * eH) / expD[data.away[i]];
        lowMu[k] = expA[data.away[i]] / expD[data.home[i]];
      }
      if (data.low.length && hi > lo) {
        rho = goldenSection((candidate) => {
          let sum = 0;
          for (let k = 0; k < data.low.length; k++) {
            const i = data.low[k];
            const t = tau(data.x[i], data.y[i], lowLambda[k], lowMu[k], candidate);
            sum += data.w[i] * Math.log(Math.max(t, TINY));
          }
          return sum;
        }, lo, hi);
      } else {
        rho = clamp(rho, lo, hi);
      }
    }
  }

  const attackOut = {};
  const defenceOut = {};
  for (let t = 0; t < teams; t++) {
    attackOut[data.ids[t]] = attack[t];
    defenceOut[data.ids[t]] = defence[t];
  }

  return {
    attack: attackOut,
    defence: defenceOut,
    homeAdv,
    rho,
    params: p,
    fittedTo: new Date(asOf).toISOString(),
    matches: n,
    competition: data.competition,
  };
}

export const dixonColes = {
  id: 'dixon-coles',
  version: 1,
  sports: DC_SPORTS,
  needsFit: true,
  defaultParams: { ...DEFAULT_PARAMS },

  /**
   * Attack and defence ratings, a home advantage and a rho, from every result
   * that had already been played at `asOf` — weighted by how long ago it was.
   *
   * `params.target` is carried onto the state and does NOT move this: the
   * likelihood is over scorelines and a closing price is not one. It is `tune`
   * that reads it. See `DC_TARGETS`.
   */
  fit(history, asOf, params = {}, { strict = false } = {}) {
    const p = { ...DEFAULT_PARAMS, ...params };
    return fitCore(assertBefore(history, asOf, strict), asOf, p);
  },

  /** P(home), P(draw) or P(away). Null for a draw-less selection or a team the fit never saw. */
  predict(state, match, selection) {
    if (selection !== 'home' && selection !== 'draw' && selection !== 'away') return null;
    const probs = marketProbs(scoreMatrix(state, match));
    return probs ? probs[selection] : null;
  },

  scoreMatrix,
  marketProbs,

  /**
   * Walk-forward search for the decay rate. Each season is predicted by a state
   * fitted only on the seasons before it, and scored by multiclass log loss
   * over the three-way result — -mean(log p) on whichever of home/draw/away
   * actually happened. The season under test never touches the fit that
   * predicts it, so a xi that only looks good in sample cannot win.
   */
  tune(history, asOf, grid = DEFAULT_GRID, { strict = false, target = DEFAULT_PARAMS.target } = {}) {
    const rows = assertBefore(history, asOf, strict)
      .filter((m) => goalsOf(m) && m.result?.winner)
      .slice()
      .sort(byKickoff);

    const seasons = [];
    for (const match of rows) {
      const s = seasonOf(match);
      if (!seasons.length || seasons[seasons.length - 1].season !== s) seasons.push({ season: s, matches: [] });
      seasons[seasons.length - 1].matches.push(match);
    }

    // One match's contribution to the loss: the surprise at what happened, or
    // the cross-entropy against where the market closed. Null where neither can
    // be read, which drops the match rather than guessing at it.
    const lossOf = (match, probs) => {
      if (!probs) return null;
      if (target !== 'close') {
        const pActual = probs[match.result.winner];
        return Number.isFinite(pActual) && pActual > 0 ? -Math.log(pActual) : null;
      }
      const close = closingView(match)?.board;
      if (!close) return null;
      let sum = 0;
      for (const side of ['home', 'draw', 'away']) {
        const q = close[side];
        const p = probs[side];
        if (!Number.isFinite(q) || q <= 0) continue;
        if (!Number.isFinite(p) || p <= 0) return null;
        sum += -q * Math.log(p);
      }
      return sum > 0 ? sum : null;
    };

    const xis = grid?.xi ?? DEFAULT_GRID.xi;
    let best = null;
    for (const xi of xis) {
      const p = { ...DEFAULT_PARAMS, xi, target };
      const perSeason = [];
      for (let i = 1; i < seasons.length; i++) {
        const cutoff = seasons[i].matches[0].kickoff;
        const train = [];
        for (let j = 0; j < i; j++) train.push(...seasons[j].matches);
        const state = fitCore(train, cutoff, p);
        let sum = 0;
        let count = 0;
        for (const match of seasons[i].matches) {
          const loss = lossOf(match, marketProbs(scoreMatrix(state, match)));
          if (loss === null) continue;
          sum += loss;
          count += 1;
        }
        if (count) perSeason.push(sum / count);
      }
      if (!perSeason.length) continue;
      const mean = perSeason.reduce((sum, x) => sum + x, 0) / perSeason.length;
      if (!best || mean < best.logLoss) best = { params: p, logLoss: mean };
    }

    return best
      ? { params: best.params, logLoss: best.logLoss, tried: xis.length, target }
      : { params: { ...DEFAULT_PARAMS, target }, logLoss: null, tried: xis.length, target };
  },
};
